/**
 * SmartAutomations — one hub for every landlord-automation feature.
 *
 * Route: /automations (landlord-only)
 *
 * Layout: 6 feature cards. Each card links to the LIVE page for that
 * feature and shows a one-shot status probe (green = ready, amber =
 * setup needed). Live features touched here:
 *   1. Automated lead nurturing        → /lead-drip
 *   2. Instant chatbot for listings    → /chatbot
 *   3. Property viewing scheduler      → /showings
 *   4. Dynamic rental pricing (NEW)    → /automations/pricing
 *   5. Smart maintenance triage (NEW)  → /tickets  (uses new /api/maintenance/triage)
 *   6. Predictive expense analytics (NEW) → /automations/expenses
 *   7. Auto lease generation           → /leases
 *   8. Auto discounts                  → /discounts
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BackToPortalButton from "@/components/BackToPortalButton";
import {
  Zap, MessageCircle, CalendarCheck, TrendingUp, Wrench, LineChart,
  FileSignature, TicketPercent, ArrowRight, ShieldCheck, Sparkles,
} from "lucide-react";

const CARDS = [
  {
    id: "lead-nurture", to: "/lead-drip",
    icon: MessageCircle, tone: "emerald", live: true,
    title: "Automated lead nurturing",
    body: "24/7 replies to listing inquiries with property answers, application links, and viewing slots — no landlord in the loop.",
    cta: "Open sequences",
  },
  {
    id: "showings", to: "/showings",
    icon: CalendarCheck, tone: "sky", live: true,
    title: "Live-calendar viewing scheduler",
    body: "Prospects self-book viewings from your Google/Outlook calendar. Auto-confirm, auto-remind, auto-follow-up.",
    cta: "Open scheduler",
  },
  {
    id: "pricing", to: "/automations/pricing",
    icon: TrendingUp, tone: "amber", live: true, isNew: true,
    title: "Dynamic rental pricing",
    body: "CMHC + local competition + vacancy pressure → a fresh rent recommendation daily, capped at ±5% for rent-control safety.",
    cta: "See recommendations",
  },
  {
    id: "triage", to: "/tickets",
    icon: Wrench, tone: "rose", live: true, isNew: true,
    title: "Smart maintenance triage",
    body: "Tenants describe the issue in text, voice, or photo. We classify urgency, tag the trade, and dispatch to your nearest vendor.",
    cta: "See open tickets",
  },
  {
    id: "leases", to: "/leases",
    icon: FileSignature, tone: "violet", live: true,
    title: "Auto lease generation",
    body: "Extract tenant data from applications, populate the province-compliant template, flag missing signatures, and drive the e-sign flow.",
    cta: "Open leases",
  },
  {
    id: "expenses", to: "/automations/expenses",
    icon: LineChart, tone: "teal", live: true, isNew: true,
    title: "Predictive expense analytics",
    body: "12-month rolling forecast per category, seasonality-aware (electricity spikes in winter, landscaping in spring). Anomaly flags built-in.",
    cta: "See forecast",
  },
  {
    id: "discounts", to: "/discounts",
    icon: TicketPercent, tone: "emerald", live: true,
    title: "Auto move-in discounts",
    body: "Set a discount once — every new lease we generate auto-applies it to first-month rent. No promo codes.",
    cta: "Open campaigns",
  },
];

export default function SmartAutomations() {
  const [status, setStatus] = useState({});
  useEffect(() => {
    // Cheap health probe — hit the pricing recommendation endpoint with
    // a known city so we can tell the user the automations are live.
    api.get("/pricing/benchmarks/toronto")
      .then(() => setStatus((s) => ({ ...s, pricing: "live" })))
      .catch(() => setStatus((s) => ({ ...s, pricing: "offline" })));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="smart-automations-page">
      <header className="bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-300">
            <Sparkles className="w-3.5 h-3.5"/> Smart Automations
          </div>
          <h1 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Every task that used to take your Sunday, now on autopilot.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl">
            Seven landlord workflows — from listing inquiry to lease e-sign to next-quarter expense forecast — all coordinated by Central Rentals so you can spend the weekend outside.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="automation-grid">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.id} to={c.to} data-testid={`automation-card-${c.id}`}
              className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-xl transition-all flex flex-col">
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 rounded-xl bg-${c.tone}-100 text-${c.tone}-700 flex items-center justify-center`}>
                  <Icon className="w-6 h-6"/>
                </div>
                <div className="flex items-center gap-1.5">
                  {c.isNew && <span className="text-[10px] uppercase tracking-widest font-bold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">New</span>}
                  {c.live && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><ShieldCheck className="w-3 h-3"/> Live</span>}
                </div>
              </div>
              <h2 className="mt-4 font-display font-extrabold text-lg text-slate-900 leading-tight">{c.title}</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{c.body}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 group-hover:text-emerald-800">
                {c.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform"/>
              </div>
            </Link>
          );
        })}
      </main>

      <div className="max-w-4xl mx-auto px-6 pb-10">
        <div className="rounded-2xl p-5 bg-emerald-50 border border-emerald-200 flex items-start gap-3" data-testid="automation-health">
          <Zap className="w-5 h-5 text-emerald-700 flex-none mt-0.5"/>
          <div className="text-sm text-emerald-900">
            <b>Health check:</b> {status.pricing === "live"
              ? "All 7 automations are running."
              : status.pricing === "offline"
                ? "Some automations are catching up — refresh in 30 seconds."
                : "Checking automations…"}
          </div>
        </div>
      </div>

      <BackToPortalButton />
    </div>
  );
}
